import { useState } from 'preact/hooks';
import { post } from '../lib/api';

interface CreateVirtualResponse {
  device: {
    uuid: string;
    identifying_string: string;
    service_type: string;
  };
}

type VCType = 'webhook' | 'mqtt' | 'requestbin';

interface VCFormProps {
  onCreated: () => void;
  onCancel: () => void;
}

export function VCForm({ onCreated, onCancel }: VCFormProps) {
  const [vcType, setVcType] = useState<VCType>('requestbin');
  const [name, setName] = useState('');
  const [startUrl, setStartUrl] = useState('');
  const [stopUrl, setStopUrl] = useState('');
  const [mqttBroker, setMqttBroker] = useState('');
  const [mqttStartTopic, setMqttStartTopic] = useState('');
  const [mqttStopTopic, setMqttStopTopic] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdHookUrl, setCreatedHookUrl] = useState<string | null>(null);

  async function handleSubmit(e: Event) {
    e.preventDefault();
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const body: Record<string, unknown> = { name: name.trim() };
      if (vcType === 'webhook') {
        body.virtualType = 'webhook';
        if (startUrl) body.startUrl = startUrl;
        if (stopUrl) body.stopUrl = stopUrl;
      } else if (vcType === 'mqtt') {
        body.virtualType = 'mqtt';
        body.mqttBroker = mqttBroker;
        body.mqttStartTopic = mqttStartTopic;
        body.mqttStopTopic = mqttStopTopic;
      } else {
        body.virtualType = 'requestbin';
      }

      const result = await post<CreateVirtualResponse & { hookUrl?: string }>('/me/device/virtual', body);
      
      if (vcType === 'requestbin' && result.device?.uuid) {
        setCreatedHookUrl(`${window.location.origin}/webhooks/${result.device.uuid}`);
      } else {
        onCreated();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create virtual device');
    } finally {
      setSubmitting(false);
    }
  }

  if (createdHookUrl) {
    return (
      <div class="card vc-success">
        <h3>Endpoint created!</h3>
        <p>Send HTTP requests to this URL:</p>
        <div class="hook-url-box">
          <code>{createdHookUrl}</code>
        </div>
        <p class="muted">POST, GET, PUT, DELETE — all captured. View logs in your dashboard.</p>
        <button class="btn btn-block" onClick={onCreated}>Done</button>
      </div>
    );
  }

  return (
    <form class="card link-form" onSubmit={handleSubmit}>
      <h3 class="link-form-title">Create virtual device</h3>

      <div class="field">
        <label htmlFor="vc-name">Name</label>
        <input
          id="vc-name"
          type="text"
          value={name}
          onInput={(e) => setName((e.target as HTMLInputElement).value)}
          placeholder="My API endpoint"
          disabled={submitting}
        />
      </div>

      <div class="field">
        <label htmlFor="vc-type">Type</label>
        <select
          id="vc-type"
          value={vcType}
          onChange={(e) => setVcType((e.target as HTMLSelectElement).value as VCType)}
        >
          <option value="requestbin">Requestbin (hosted endpoint)</option>
          <option value="webhook">Webhook (HTTP POST to your URL)</option>
          <option value="mqtt">MQTT (publish to broker)</option>
        </select>
      </div>

      {vcType === 'requestbin' && (
        <p class="muted vc-hint">
          Hermes hosts a public URL that captures all incoming requests.
          Use it to demo pay-per-trigger or debug webhooks.
        </p>
      )}

      {vcType === 'webhook' && (
        <>
          <div class="field">
            <label htmlFor="vc-start-url">Start URL (POST)</label>
            <input
              id="vc-start-url"
              type="url"
              value={startUrl}
              onInput={(e) => setStartUrl((e.target as HTMLInputElement).value)}
              placeholder="https://your-server.com/start"
              disabled={submitting}
            />
          </div>
          <div class="field">
            <label htmlFor="vc-stop-url">Stop URL (POST, optional)</label>
            <input
              id="vc-stop-url"
              type="url"
              value={stopUrl}
              onInput={(e) => setStopUrl((e.target as HTMLInputElement).value)}
              placeholder="https://your-server.com/stop"
              disabled={submitting}
            />
          </div>
        </>
      )}

      {vcType === 'mqtt' && (
        <>
          <div class="field">
            <label htmlFor="vc-mqtt-broker">MQTT Broker (wss://)</label>
            <input
              id="vc-mqtt-broker"
              type="url"
              value={mqttBroker}
              onInput={(e) => setMqttBroker((e.target as HTMLInputElement).value)}
              placeholder="wss://broker.example.com:8884/mqtt"
              disabled={submitting}
            />
          </div>
          <div class="field">
            <label htmlFor="vc-mqtt-start">Start Topic</label>
            <input
              id="vc-mqtt-start"
              type="text"
              value={mqttStartTopic}
              onInput={(e) => setMqttStartTopic((e.target as HTMLInputElement).value)}
              placeholder="device/command/on"
              disabled={submitting}
            />
          </div>
          <div class="field">
            <label htmlFor="vc-mqtt-stop">Stop Topic</label>
            <input
              id="vc-mqtt-stop"
              type="text"
              value={mqttStopTopic}
              onInput={(e) => setMqttStopTopic((e.target as HTMLInputElement).value)}
              placeholder="device/command/off"
              disabled={submitting}
            />
          </div>
        </>
      )}

      {error && <div class="alert alert-error">{error}</div>}

      <div class="vc-actions">
        <button
          type="submit"
          class="btn btn-block"
          disabled={submitting || !name.trim()}
        >
          {submitting ? 'Creating…' : 'Create device'}
        </button>
        <button
          type="button"
          class="btn btn-secondary btn-block"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
